// Sheet (DESIGN §3.7.1): Radix Dialog als Seitenebene. Desktop von rechts (Breite `sheet` 480 bzw.
// `sheet-wide` 640, 8 Abstand, Radius 16, `card`, `shadow-3`), mobil von unten (max. 92 % Höhe,
// Radius oben 20, Griff, Wischen nach unten schließt). Kopf 64 → Inhalt (scrollt, 24) → Fuß 72.
// Ungespeicherte Änderungen (`dirty`) fragen vor dem Schließen nach („Discard changes?“).
import { useRef, useState, type ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useSwipeToClose } from './_lib/useSwipeToClose';
import { ConfirmDialog, scrimClass } from './Dialog';
import { IconButton } from './IconButton';
import './_lib/motion.css';
import { LayerProvider } from './_lib/layer';
import { cn } from '../basis/kern';

export type SheetSide = 'right' | 'bottom' | 'left';

export interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Auslöser (optional, sonst kontrolliert über `open`). */
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** `right` (Standard; mobil von unten), `bottom`, `left` (Drawer der Navigation). */
  side?: SheetSide;
  size?: 'default' | 'wide';
  /** Aktionen im Fuß (Sekundär „Cancel“ zuerst, Primär zuletzt). */
  footer?: ReactNode;
  /** Ungespeicherte Änderungen: Schließen fragt nach. */
  dirty?: boolean;
  className?: string;
  children?: ReactNode;
}

const sideClass: Record<SheetSide, string> = {
  right: cn(
    'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl',
    'md:inset-x-auto md:top-2 md:right-2 md:bottom-2 md:max-h-none md:rounded-xl',
  ),
  bottom:
    'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl md:inset-x-auto md:left-1/2 md:w-dialog-l md:-translate-x-1/2',
  left: 'top-2 bottom-2 left-2 w-[min(320px,calc(100vw-16px))] rounded-xl',
};

export const SheetClose = DialogPrimitive.Close;

export function Sheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  side = 'right',
  size = 'default',
  footer,
  dirty = false,
  className,
  children,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [innerOpen, setInnerOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const isOpen = open ?? innerOpen;

  const commit = (next: boolean) => {
    if (open === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  };
  const requestChange = (next: boolean) => {
    if (!next && dirty) setConfirmDiscard(true);
    else commit(next);
  };
  const swipe = useSwipeToClose(panelRef, () => requestChange(false), side !== 'left');
  const fromBottom = side === 'bottom' || side === 'right';

  return (
    <>
      <DialogPrimitive.Root open={isOpen} onOpenChange={requestChange}>
        {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className={cn(scrimClass, 'z-(--bar-z-sheet)')} />
          <DialogPrimitive.Content
            ref={panelRef}
            data-side={side}
            className={cn(
              'bar-st-sheet fixed z-(--bar-z-sheet) flex flex-col bg-card text-fg shadow-3 focus-visible:outline-none',
              sideClass[side],
              side !== 'left' && (size === 'wide' ? 'md:w-sheet-wide' : 'md:w-sheet'),
              'md:max-w-[calc(100vw-16px)]',
              className,
            )}
            {...(description ? {} : { 'aria-describedby': undefined })}
          >
            <LayerProvider z="var(--bar-z-sheet)">
              {fromBottom ? (
                <div
                  {...swipe}
                  aria-hidden="true"
                  className={cn(
                    'flex h-5 shrink-0 cursor-grab touch-none items-end justify-center',
                    side === 'right' && 'md:hidden',
                  )}
                >
                  <span className="h-1 w-9 rounded-full bg-fg-decor" />
                </div>
              ) : null}
              <header className="flex min-h-16 shrink-0 items-center gap-3 px-6 py-3">
                <div className="min-w-0 flex-1">
                  <DialogPrimitive.Title className="truncate text-title-detail text-fg">
                    {title}
                  </DialogPrimitive.Title>
                  {description ? (
                    <DialogPrimitive.Description className="truncate text-label text-fg-secondary">
                      {description}
                    </DialogPrimitive.Description>
                  ) : null}
                </div>
                <DialogPrimitive.Close asChild>
                  <IconButton
                    label="Close"
                    icon={<XIcon />}
                    variant="ghost"
                    surface="card"
                    tooltip={false}
                    className="-mr-2"
                  />
                </DialogPrimitive.Close>
              </header>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>
              {footer ? (
                <footer className="flex min-h-18 shrink-0 items-center justify-end gap-3 border-t border-line-subtle px-6 py-4 max-md:flex-col-reverse max-md:items-stretch max-md:pb-[max(16px,env(safe-area-inset-bottom))]">
                  {footer}
                </footer>
              ) : null}
            </LayerProvider>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard changes?"
        description="Your unsaved changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={() => commit(false)}
      />
    </>
  );
}
